import React, { useState } from "react";
import "../../Styles/DeepamSchemeForm.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { deepamSchemeAPI } from "../../api/index";

// Where to go after a successful submit.
// For the Admin dashboard change this to "/AdminDashboard/DeepamSchemeTable".
const TABLE_ROUTE = "/dashboard/DeepamSchemeTable";

const MANDAL_NAMES = ["Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

const INITIAL_FORM = {
  headOfHousehold: "",
  relation: "",
  rationCardNumber: "",
  gasConnectionNumber: "",
  gasAgency: "",
  aadhaar: "",
  voterId: "",
  mobile: "",
  mandal: "",
  village: "",
  date: "",
  status: "",
  photoUrl: "",
  documentUrl: "",
  remarks: "",
};

// Same order as the form layout (used to focus the first invalid field on submit)
const FIELD_ORDER = [
  "headOfHousehold",
  "relation",
  "rationCardNumber",
  "gasConnectionNumber",
  "gasAgency",
  "aadhaar",
  "voterId",
  "mobile",
  "mandal",
  "village",
  "date",
  "status",
  "photoUrl",
  "documentUrl",
  "remarks",
];

// Fields that must be filled (these all have a * on the form).
// Photo URL, Supporting Document URL and Remarks are optional, but if they are
// filled they must still be valid.
const REQUIRED_FIELDS = [
  "headOfHousehold",
  "relation",
  "rationCardNumber",
  "gasConnectionNumber",
  "gasAgency",
  "aadhaar",
  "voterId",
  "mobile",
  "mandal",
  "village",
  "date",
  "status",
];

// These fields show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = ["aadhaar", "voterId", "mobile", "village"];

// Digits only (other characters are blocked automatically)
const DIGITS_ONLY_FIELDS = ["aadhaar", "mobile"];

// Converted to capitals automatically
const UPPERCASE_FIELDS = ["voterId", "rationCardNumber", "gasConnectionNumber"];

const NAME_REGEX = /^[A-Za-z\s.']+$/;
const RELATION_REGEX = /^[A-Za-z\s./'\-]+$/;
const RATION_CARD_REGEX = /^[A-Z0-9\-\/]+$/;
const GAS_CONNECTION_REGEX = /^[A-Z0-9\-\/]+$/;
const AGENCY_REGEX = /^[A-Za-z0-9\s.,\-&'()\/]+$/;
const VILLAGE_REGEX = /^[A-Za-z\s]+$/;
const VOTER_ID_REGEX = /^[A-Z]{3}[0-9]{7}$/;
const AADHAAR_REGEX = /^[2-9][0-9]{11}$/;
const MOBILE_REGEX = /^[6-9][0-9]{9}$/;

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

// "1111111111" / "999999999999" style numbers are not real
const isRepeatedDigit = (s) => /^(\d)\1+$/.test(s);

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;
  const required = REQUIRED_FIELDS.includes(name);

  switch (name) {
    case "headOfHousehold":
      if (!v) return "Head of Household name is required";
      if (v.length < 3) return "Name must be at least 3 characters";
      if (v.length > 100) return "Name must not exceed 100 characters";
      if (!NAME_REGEX.test(v)) return "Name can contain only alphabets, spaces, '.' and '''";
      return "";

    case "relation":
      if (!v) return required ? "Relation is required (e.g. W/O Ramesh)" : "";
      if (v.length < 2) return "Relation must be at least 2 characters";
      if (v.length > 100) return "Relation must not exceed 100 characters";
      if (!RELATION_REGEX.test(v)) return "Relation can contain only alphabets, spaces and . / ' -";
      if ((v.match(/[A-Za-z]/g) || []).length < 2)
        return "Relation must contain at least 2 letters (e.g. W/O Ramesh)";
      return "";

    case "rationCardNumber":
      if (!v) return "Ration Card number is required";
      if (v.length < 6) return "Ration Card number must be at least 6 characters";
      if (v.length > 20) return "Ration Card number must not exceed 20 characters";
      if (!RATION_CARD_REGEX.test(v))
        return "Ration Card number can contain only letters, digits, '-' and '/'";
      return "";

    case "gasConnectionNumber":
      if (!v) return "Gas Connection number is required";
      if (v.length < 5) return "Gas Connection number must be at least 5 characters";
      if (v.length > 20) return "Gas Connection number must not exceed 20 characters";
      if (!GAS_CONNECTION_REGEX.test(v))
        return "Gas Connection number can contain only letters, digits, '-' and '/'";
      return "";

    case "gasAgency":
      if (!v) return "Gas Agency is required";
      if (v.length < 3) return "Gas Agency name must be at least 3 characters";
      if (v.length > 100) return "Gas Agency name must not exceed 100 characters";
      if (!AGENCY_REGEX.test(v))
        return "Agency name can contain only letters, numbers, spaces and . , - & ' ( ) /";
      return "";

    case "aadhaar":
      if (!v) return required ? "Aadhaar number is required" : "";
      if (!/^\d+$/.test(v)) return "Aadhaar number must contain digits only";
      if (v.length < 12) return `Aadhaar number must be exactly 12 digits (${v.length}/12 entered)`;
      if (v.length > 12) return "Aadhaar number must be exactly 12 digits";
      if (!AADHAAR_REGEX.test(v)) return "Aadhaar number cannot start with 0 or 1";
      if (isRepeatedDigit(v)) return "Enter a valid Aadhaar number";
      return "";

    case "voterId":
      if (!v) return required ? "Voter Id is required" : "";
      if (v.length < 10) return `Voter Id must be 10 characters (${v.length}/10 entered)`;
      if (!VOTER_ID_REGEX.test(v))
        return "Enter a valid Voter Id (3 letters + 7 digits, e.g. ABC1234567)";
      return "";

    case "mobile":
      if (!v) return required ? "Mobile number is required" : "";
      if (!/^\d+$/.test(v)) return "Mobile number must contain digits only";
      if (!/^[6-9]/.test(v)) return "Mobile number must start with 6, 7, 8 or 9";
      if (v.length < 10) return `Mobile number must be exactly 10 digits (${v.length}/10 entered)`;
      if (v.length > 10) return "Mobile number must be exactly 10 digits";
      if (!MOBILE_REGEX.test(v)) return "Enter a valid mobile number";
      if (isRepeatedDigit(v)) return "Enter a valid mobile number";
      return "";

    case "mandal":
      if (!v) return "Please select a Mandal";
      if (!MANDAL_NAMES.includes(v)) return "Please select a valid Mandal";
      return "";

    case "village":
      if (!v) return "Village is required";
      if (!VILLAGE_REGEX.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "date": {
      if (!v) return required ? "Date is required" : "";
      if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return "Enter a valid date";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUS_OPTIONS.includes(v)) return "Please select a valid Status";
      return "";

    case "photoUrl":
    case "documentUrl":
      if (!v) return ""; // optional
      if (!isLikelyUrl(v)) return "Enter a valid link starting with http:// or https://";
      return "";

    case "remarks":
      if (!v) return ""; // optional
      if (v.length < 5) return "Remarks must be at least 5 characters";
      if (v.length > 500) return "Remarks must not exceed 500 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (data) => {
  const errors = {};
  FIELD_ORDER.forEach((name) => {
    const msg = validateField(name, data[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

const DeepamSchemeForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Auto-format specific fields
    if (UPPERCASE_FIELDS.includes(name)) {
      value = value.toUpperCase().replace(/\s/g, "");
    }
    if (DIGITS_ONLY_FIELDS.includes(name)) {
      value = value.replace(/\D/g, "");
    }

    // Changing the Mandal clears the Village (it must belong to the chosen Mandal)
    if (name === "mandal") {
      setFormData((prev) => ({ ...prev, mandal: value, village: "" }));
      setErrors((prev) => ({ ...prev, mandal: validateField("mandal", value), village: "" }));
      setTouched((prev) => ({ ...prev, mandal: true, village: false }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched(FIELD_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f]);
    if (firstInvalid) {
      toast.error("Please fix the highlighted fields");
      const el = document.querySelector(`[name="${firstInvalid}"]`);
      if (el) el.focus();
      return;
    }

    const payload = {
      head_of_household_name: formData.headOfHousehold.trim(),
      relation_name: formData.relation.trim(),
      ration_card_number: formData.rationCardNumber.trim(),
      gas_connection_number: formData.gasConnectionNumber.trim(),
      gas_agency: formData.gasAgency.trim(),
      aadhaar_number: formData.aadhaar.trim(),
      epic_no: formData.voterId.trim(),
      mobile_number: formData.mobile.trim(),
      mandal_name: formData.mandal,
      village_name: formData.village.trim(),
      application_date: formData.date,
      status: formData.status,
      photo_url: formData.photoUrl.trim() || undefined,
      document_url: formData.documentUrl.trim() || undefined,
      remarks: formData.remarks.trim() || undefined,
    };

    setSubmitting(true);
    try {
      console.log("Deepam Scheme create payload sent:", payload);
      await deepamSchemeAPI.create(payload);

      // Success toast — stays visible after navigating to the table page
      toast.success("Form submitted successfully", { toastId: "deepam-form-success" });

      handleReset();
      navigate(TABLE_ROUTE);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="deepam-form-container">
      <div className="deepam-form-card">
        <div className="deepam-form-header">
          <h2>Add New Deepam Scheme Beneficiary</h2>
          <p>Three free LPG cylinders every year for each eligible household</p>
          <button type="button" className="deepam-back-btn" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>

        {/* noValidate -> disables browser default popups so our custom messages show */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="deepam-form-grid">
            <div className="deepam-form-group">
              <label>Head of Household Name *</label>
              <input
                type="text"
                name="headOfHousehold"
                placeholder="Enter Name"
                value={formData.headOfHousehold}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("headOfHousehold")}
                maxLength={100}
              />
              {showError("headOfHousehold") && (
                <span className="error-text">{errors.headOfHousehold}</span>
              )}
            </div>

            <div className="deepam-form-group">
              <label>Relation (Ex: W/O, S/O) *</label>
              <input
                type="text"
                name="relation"
                placeholder="Ex: W/O Ramesh, S/O Suresh"
                value={formData.relation}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("relation")}
                maxLength={100}
              />
              {showError("relation") && <span className="error-text">{errors.relation}</span>}
            </div>

            <div className="deepam-form-group">
              <label>Ration Card Number *</label>
              <input
                type="text"
                name="rationCardNumber"
                placeholder="Enter Ration Card Number"
                value={formData.rationCardNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("rationCardNumber")}
                maxLength={20}
              />
              {showError("rationCardNumber") && (
                <span className="error-text">{errors.rationCardNumber}</span>
              )}
            </div>

            <div className="deepam-form-group">
              <label>Gas Connection Number *</label>
              <input
                type="text"
                name="gasConnectionNumber"
                placeholder="Enter Gas Connection Number"
                value={formData.gasConnectionNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("gasConnectionNumber")}
                maxLength={20}
              />
              {showError("gasConnectionNumber") && (
                <span className="error-text">{errors.gasConnectionNumber}</span>
              )}
            </div>

            <div className="deepam-form-group">
              <label>Gas Agency *</label>
              <input
                type="text"
                name="gasAgency"
                placeholder="Enter Gas Agency Name"
                value={formData.gasAgency}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("gasAgency")}
                maxLength={100}
              />
              {showError("gasAgency") && <span className="error-text">{errors.gasAgency}</span>}
            </div>

            <div className="deepam-form-group">
              <label>Aadhaar Number *</label>
              <input
                type="text"
                inputMode="numeric"
                name="aadhaar"
                placeholder="Enter 12-digit Aadhaar Number"
                value={formData.aadhaar}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("aadhaar")}
                maxLength={12}
              />
              {showError("aadhaar") && <span className="error-text">{errors.aadhaar}</span>}
            </div>

            <div className="deepam-form-group">
              <label>Voter Id *</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter Voter Id (e.g. ABC1234567)"
                value={formData.voterId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("voterId")}
                maxLength={10}
              />
              {showError("voterId") && <span className="error-text">{errors.voterId}</span>}
            </div>

            <div className="deepam-form-group">
              <label>Mobile Number *</label>
              <input
                type="tel"
                inputMode="numeric"
                name="mobile"
                placeholder="Enter 10-digit Mobile Number"
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mobile")}
                maxLength={10}
              />
              {showError("mobile") && <span className="error-text">{errors.mobile}</span>}
            </div>

            {/* Mandal first ... */}
            <div className="deepam-form-group">
              <label>Mandal *</label>
              <select
                name="mandal"
                value={formData.mandal}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mandal")}
              >
                <option value="">Select Mandal</option>
                {MANDAL_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {showError("mandal") && <span className="error-text">{errors.mandal}</span>}
            </div>

            {/* ... then Village */}
            <div className="deepam-form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder={formData.mandal ? "Enter Village (alphabets only)" : "Select Mandal first"}
                value={formData.village}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("village")}
                maxLength={100}
                disabled={!formData.mandal}
              />
              {showError("village") && <span className="error-text">{errors.village}</span>}
              <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Must exactly match an existing village name under the selected Mandal.
              </small>
            </div>

            <div className="deepam-form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("date")}
                min="1990-01-01"
                max="2100-12-31"
              />
              {showError("date") && <span className="error-text">{errors.date}</span>}
            </div>

            <div className="deepam-form-group">
              <label>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("status")}
              >
                <option value="">Select Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
              {showError("status") && <span className="error-text">{errors.status}</span>}
            </div>

            <div className="deepam-form-group">
              <label>Photo URL (Optional)</label>
              <input
                type="url"
                name="photoUrl"
                placeholder="https://..."
                value={formData.photoUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("photoUrl")}
              />
              {showError("photoUrl") && <span className="error-text">{errors.photoUrl}</span>}
            </div>

            <div className="deepam-form-group">
              <label>Supporting Document URL (Optional)</label>
              <input
                type="url"
                name="documentUrl"
                placeholder="https://..."
                value={formData.documentUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("documentUrl")}
              />
              {showError("documentUrl") && <span className="error-text">{errors.documentUrl}</span>}
            </div>
          </div>

          <div className="deepam-form-group deepam-full-width">
            <label>Remarks (Optional)</label>
            <textarea
              rows="4"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("remarks")}
              maxLength={500}
            ></textarea>
            {showError("remarks") && <span className="error-text">{errors.remarks}</span>}
            <small className="char-count">{formData.remarks.length}/500</small>
          </div>

          <div className="deepam-btn-group">
            <button type="submit" className="deepam-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="deepam-reset-btn"
              onClick={handleReset}
              disabled={submitting}
            >
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeepamSchemeForm;